import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Download, UserRound, FolderGit2, GraduationCap, Wrench, Award,
  CircleCheck, CircleAlert,
} from 'lucide-react';
import { AsyncBoundary, useToast } from '@kollez/design-system';
import { useAuthStore } from '@/lib/authStore';
import {
  useResumes, useResume, useCreateResume, usePatchResume, useRunAts,
} from '@/lib/queries';

/**
 * /resume-builder (F94a) — direct port of kollez/pages/student/resume-builder.html.
 *   2-col: left form (score ring + fixes list + accordion sections)
 *   right sticky .rs paper preview with yellow-highlighted keywords for tailored targets.
 *   Header · Projects · Education · Skills · Certificates accordion (details/summary + body).
 */
export function StudentResumeBuilderPage() {
  const { user } = useAuthStore();
  const list = useResumes();
  const create = useCreateResume();
  const [target, setTarget] = useState('');
  const toast = useToast();

  // Pick primary or first resume
  const primary = list.data?.resumes.find((r) => r.isPrimary) ?? list.data?.resumes[0];
  const [activeId, setActiveId] = useState<string | null>(null);
  useEffect(() => {
    if (!activeId && primary) setActiveId(primary._id);
  }, [primary, activeId]);

  const detail = useResume(activeId ?? undefined);
  const resume = detail.data?.resume;
  const patch = usePatchResume(activeId ?? '');
  const runAts = useRunAts(activeId ?? '');

  // Local draft mirrors the resume so typing feels instant
  const [draft, setDraft] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!resume) return;
    setDraft({
      nm: user?.fullName ?? '',
      hd: resume.sections?.summary ?? '',
      p1: resume.sections?.projects?.[0]?.bullets?.[0] ?? '',
      p2: resume.sections?.projects?.[1]?.bullets?.[0] ?? '',
      sk: (resume.sections?.skills ?? []).join(', '),
      ac: (resume.sections?.certifications ?? []).map((c) => c.name).join(' · '),
    });
  }, [resume, user?.fullName]);

  const score = resume?.atsCheck?.score ?? 60;
  const findings = useMemo(() => resume?.atsCheck?.findings ?? [], [resume?.atsCheck]);

  async function ensureResume() {
    if (activeId) return activeId;
    try {
      const r = await create.mutateAsync({
        name: 'My resume',
        sections: {
          summary: draft.hd,
          skills: (draft.sk ?? '').split(',').map((s) => s.trim()).filter(Boolean),
        },
      });
      setActiveId(r.resume._id);
      return r.resume._id;
    } catch {
      toast.show({ tone: 'warning', message: 'Could not create resume' });
      return null;
    }
  }

  async function saveField(k: string, v: string) {
    setDraft((d) => ({ ...d, [k]: v }));
    if (!activeId) return;
    const sections: Partial<NonNullable<typeof resume>['sections']> = {};
    if (k === 'hd') sections.summary = v;
    if (k === 'sk') sections.skills = v.split(',').map((s) => s.trim()).filter(Boolean);
    if (k === 'p1' || k === 'p2') {
      const which = k === 'p1' ? 0 : 1;
      const projects = [...(resume?.sections?.projects ?? [])];
      const cur = projects[which] ?? { name: which === 0 ? 'Project 1' : 'Project 2' };
      projects[which] = { ...cur, bullets: [v] };
      sections.projects = projects;
    }
    if (k === 'ac') {
      sections.certifications = v.split(' · ').map((n) => ({ name: n.trim() })).filter((c) => c.name);
    }
    if (Object.keys(sections).length) {
      try { await patch.mutateAsync({ sections }); } catch { /* debounce would fix this later */ }
    }
  }

  async function refreshScore() {
    const id = await ensureResume();
    if (!id) return;
    try {
      await runAts.mutateAsync();
      toast.show({ message: 'Nexie re-scored your resume.' });
    } catch { toast.show({ tone: 'warning', message: 'Could not run the check.' }); }
  }

  function showFixHint(index: number) {
    // Deep-audit fix (Page 13/N): the old showFixHint() wrote fabricated
    // project descriptions ("wrote the ESP32 firmware and calibrated
    // 12 soil sensors…") directly into the student's live resume.
    // That's actively harmful — a student could accept the injection
    // and submit fictional projects to a recruiter. Now the fix-hint
    // click just shows Nexie's suggestion text as guidance; the student
    // writes it themselves.
    const f = findings[index];
    if (!f) return;
    toast.show({
      message: f.fixHint
        ? `Hint: ${f.fixHint}. Type the change yourself so it reflects what you actually did.`
        : 'Rewrite this section yourself with real details.',
    });
  }

  // Preview keywords — target-tailoring dropped (see above). Empty set means
  // no yellow highlight; the preview renders plain text until a real keyword
  // source (per-Opportunity or per-Resume.variantOf) exists.
  const keywords = useMemo(() => {
    void target;
    return new Set<string>();
  }, [target]);

  function hlSkills(csv: string): React.ReactNode {
    const parts = csv.split(/([,\s]+)/);
    return parts.map((t, i) =>
      keywords.has(t.trim())
        ? <span key={i} className="hl">{t}</span>
        : <span key={i}>{t}</span>,
    );
  }

  return (
    <div className="stack">
      <style>{`
        .rb { display: grid; gap: 20px; grid-template-columns: minmax(0,1fr); align-items: start; }
        @media (min-width: 1100px) { .rb { grid-template-columns: minmax(0,1fr) minmax(0,1fr); } .rb-prev { position: sticky; top: 76px; } }
        .rs { background: #fff; color: #1a1d2b; border: 1px solid rgb(var(--line-tertiary)); border-radius: 4px; box-shadow: var(--shadow-md); padding: 30px 32px; font-size: 12.5px; line-height: 1.5; }
        .rs h4 { font-size: 10.5px; letter-spacing: .1em; text-transform: uppercase; color: #5b6070; margin: 14px 0 4px; border-bottom: 1px solid #e3e6ee; padding-bottom: 3px; }
        .rs .nm { font-family: "Newsreader", Georgia, serif; font-size: 24px; line-height: 1.1; }
        .rs ul { margin: 2px 0 0; padding-left: 16px; }
        .rs .hl { background: rgb(214 170 40 / .25); border-radius: 2px; }
        .sec-ed { border: 1px solid rgb(var(--line-tertiary)); border-radius: 10px; background: rgb(var(--bg-primary)); }
        .sec-ed > summary { list-style: none; cursor: pointer; padding: 12px 16px; display: flex; align-items: center; gap: 10px; font-weight: 600; }
        .sec-ed > summary::-webkit-details-marker { display: none; }
        .sec-ed[open] > summary { border-bottom: 1px solid rgb(var(--line-tertiary)); }
        .sec-ed .body { padding: 14px 16px; display: grid; gap: 12px; }
        .score { display: grid; grid-template-columns: auto 1fr; gap: 14px; align-items: center; }
        @media (max-width: 1099px) { .rb-prev { order: -1; } }
      `}</style>

      <div className="page-header">
        <div className="ph-text">
          <h1 className="h-page">Resume builder</h1>
          <p className="subhead">One page, the format Indian campus recruiters expect. Nexie suggests; you decide every word.</p>
        </div>
        <div className="ph-actions">
          {/* Tailored-target picker deferred — needs a real resume variant per Opportunity
              plus recruiter-specific keyword sets. Hardcoded Deloitte/Wipro options and
              their fixed keyword highlight ({SQL,Python,consulting,analyst} /
              {Java,Spring,AWS,testing}) dropped; single General variant only. */}
          <button
            className="btn btn-primary"
            disabled
            title="PDF export ships in the next release. Use browser Print → Save as PDF for now."
          >
            <Download size={14} />Download PDF · soon
          </button>
        </div>
      </div>

      <AsyncBoundary
        kind="detail"
        loading={list.isLoading}
        error={list.error ?? detail.error}
        onRetry={() => { void list.refetch(); if (activeId) void detail.refetch(); }}
      >
        <div className="rb">
          <div className="grid gap-3 min-w-0">
            {/* Score */}
            <div className="card card-pad score">
              <div className="ring" style={{ ['--v' as string]: score } as React.CSSProperties}>
                <span style={{ fontSize: '.9375rem' }}>{score}</span>
              </div>
              <div>
                <div className="font-semibold">Resume check</div>
                <div className="helper">
                  {score >= 90
                    ? 'Strong. Ready to send.'
                    : `Clear and readable. ${findings.length} fix${findings.length === 1 ? '' : 'es'} would make it stronger.`}
                </div>
                <button className="link text-sm" onClick={refreshScore}>Re-check with Nexie</button>
              </div>
            </div>

            {/* Fixes list */}
            {findings.length > 0 && (
              <div className="card card-pad grid gap-2">
                {findings.map((f, i) => {
                  const ok = f.kind === 'suggestion' && !f.fixHint;
                  const Icon = ok ? CircleCheck : CircleAlert;
                  return (
                    <div key={i} className={`qcheck ${ok ? 'ok' : f.kind === 'error' ? 'bad' : 'warn'}`}>
                      <Icon size={14} />
                      <span className="flex-1">
                        {f.message}
                        {f.fixHint && (
                          <>
                            {' '}<button className="link text-sm" onClick={() => showFixHint(i)}>{f.fixHint}</button>
                          </>
                        )}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}

            {/* Header */}
            <details className="sec-ed" open>
              <summary><UserRound size={14} />Header</summary>
              <div className="body">
                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="grid gap-1.5">
                    <span className="label">Name</span>
                    <input className="input" value={draft.nm ?? ''} onChange={(e) => saveField('nm', e.target.value)} />
                  </label>
                  <label className="grid gap-1.5">
                    <span className="label">Headline</span>
                    <input className="input" value={draft.hd ?? ''} onChange={(e) => saveField('hd', e.target.value)} />
                  </label>
                </div>
                <p className="helper m-0">Email, phone and LinkedIn come from your profile.</p>
              </div>
            </details>

            {/* Projects */}
            <details className="sec-ed" open>
              <summary><FolderGit2 size={14} />Projects</summary>
              <div className="body">
                <label className="grid gap-1.5">
                  <span className="label">Project 1 — one impact sentence</span>
                  <textarea
                    className="textarea"
                    rows={3}
                    placeholder="What you built, the technology, and the measurable result."
                    value={draft.p1 ?? ''}
                    onChange={(e) => saveField('p1', e.target.value)}
                  />
                </label>
                <label className="grid gap-1.5">
                  <span className="label">Project 2 — one impact sentence</span>
                  <textarea
                    className="textarea"
                    rows={2}
                    placeholder="What you built, the technology, and the measurable result."
                    value={draft.p2 ?? ''}
                    onChange={(e) => saveField('p2', e.target.value)}
                  />
                </label>
                <p className="helper m-0">
                  Add real numbers when you can — "cut latency by 40 ms", "handled 300 users a week".
                </p>
              </div>
            </details>

            {/* Education */}
            <details className="sec-ed">
              <summary><GraduationCap size={14} />Education</summary>
              <div className="body">
                <p className="helper m-0">Filled from your verified academic record. <Link className="link" to="/profile">View</Link></p>
              </div>
            </details>

            {/* Skills */}
            <details className="sec-ed">
              <summary><Wrench size={14} />Skills</summary>
              <div className="body">
                <input className="input" value={draft.sk ?? ''} onChange={(e) => saveField('sk', e.target.value)} />
                <p className="helper m-0">Order matters: recruiters read the first three.</p>
              </div>
            </details>

            {/* Certificates */}
            <details className="sec-ed">
              <summary><Award size={14} />Certificates and achievements</summary>
              <div className="body">
                <textarea className="textarea" rows={2} value={draft.ac ?? ''} onChange={(e) => saveField('ac', e.target.value)} />
              </div>
            </details>
          </div>

          {/* Preview */}
          <div className="rb-prev grid gap-2">
            <div className="flex items-center justify-between">
              <span className="eyebrow">Preview · A4 · 1 page</span>
              <span className="helper">Readable by applicant tracking systems</span>
            </div>
            <div className="rs">
              <div className="nm">{draft.nm || user?.fullName || 'Your name'}</div>
              <div style={{ color: '#5b6070' }}>
                {draft.hd || 'Your headline'} · {user?.email ?? 'your.email@college.edu.in'}
              </div>
              {draft.hd && (
                <>
                  <h4>Summary</h4>
                  <div><span className={target === 'dl' ? 'hl' : ''}>{draft.hd}</span></div>
                </>
              )}
              <h4>Education</h4>
              <div>
                <b>B.Tech</b> — as per your verified academic record
              </div>
              {(draft.p1 || draft.p2) && (
                <>
                  <h4>Projects</h4>
                  {draft.p1 && (
                    <>
                      <div><b>Smart irrigation controller</b> · final-year</div>
                      <ul><li>{draft.p1}</li></ul>
                    </>
                  )}
                  {draft.p2 && (
                    <div style={{ marginTop: 4 }}>
                      <div><b>Traffic density estimator</b> · personal</div>
                      <ul><li>{draft.p2}</li></ul>
                    </div>
                  )}
                </>
              )}
              {draft.sk && (
                <>
                  <h4>Skills</h4>
                  <div>{hlSkills(draft.sk)}</div>
                </>
              )}
              {draft.ac && (
                <>
                  <h4>Certificates and achievements</h4>
                  <div>{draft.ac}</div>
                </>
              )}
            </div>
          </div>
        </div>
      </AsyncBoundary>
    </div>
  );
}
